import React, {useRef, useState} from 'react'
import {Text, Box, Link as CoreLink, slugify} from '@miro-site/core'
import styled from 'styled-components'

const Title = styled(CoreLink).attrs(props => ({
  $mb: props.theme.header.secondaryNavigation.item.margins.afterTitle,
  $styles: {
    ...props.theme.header.secondaryNavigation.item.title.styles,
  },
}))``

const Description = styled(Text).attrs(props => ({
  as: 'p',
  $styles: {
    ...props.theme.header.secondaryNavigation.item.description.styles,
  },
}))``

const Wrapper = styled(Box).attrs<{$isFocused?: boolean}>(
  ({
    $isFocused,
    theme: {
      focus,
      header: {
        secondaryNavigation: {item},
      },
    },
  }) => {
    const visibleAnimation = {
      opacity: 1,
      transform: 'scale(1)',
      transition: item.hover.animation.show,
    }

    const hoverStyles = {
      [Title]: item.title.hoverStyles,
      [Description]: item.description.hoverStyles,
    }

    return {
      $styles: {
        display: 'block',
        position: 'relative',
        marginTop: item.margins.before,
        marginBottom: item.margins.after,
        maxWidth: item.maxWidth,
        cursor: 'pointer',
        ':before': {
          position: 'absolute',
          content: "''",
          transform: 'scale(.9)',
          zIndex: -1,
          opacity: 0,
          transition: item.hover.animation.hide,
          width: `calc(100% + ${item.hover.offset.left + item.hover.offset.right}px)`,
          height: `calc(100% + ${item.hover.offset.top + item.hover.offset.bottom}px)`,
          top: `-${item.hover.offset.top}px`,
          left: `-${item.hover.offset.left}px`,
          borderRadius: item.hover.radius,
          background: item.hover.background,
          border: '1px solid transparent',
          ...($isFocused ? {...focus.outlines.default, ...visibleAnimation} : {}),
        },
        ':hover': {
          ':before': {
            ...visibleAnimation,
          },
          ...hoverStyles,
        },
        ...($isFocused ? hoverStyles : {}),
      },
    }
  },
)<{$isFocused?: boolean}>``

interface SecondaryNavigationItemProps {
  title: string
  description?: string | null
  path: string
  className?: string
}

const SecondaryNavigationItem = (props: SecondaryNavigationItemProps): React.ReactElement => {
  const {title, description, path, className} = props

  const linkRef = useRef<HTMLAnchorElement>(null)

  const descriptionId = `${slugify(title)}-description`

  const [isFocused, setFocused] = useState(false)

  const handleClick: React.MouseEventHandler = e => {
    if (!linkRef.current?.contains(e.target as Node)) {
      linkRef.current?.click()
    }
  }

  return (
    <Wrapper $isFocused={isFocused} className={className} onClick={handleClick}>
      <Title
        ref={linkRef}
        onFocus={e => {
          if (e.target === linkRef.current) {
            setFocused(true)
          }
        }}
        onBlur={() => setFocused(false)}
        href={path}
        aria-describedby={descriptionId}
      >
        {title}
      </Title>
      <Description id={descriptionId}>{description}</Description>
    </Wrapper>
  )
}

export default SecondaryNavigationItem
